import type { components } from "#open-fetch-schemas/atlit";
import type { IEvent, IPlaylist, IPlaylistItem, IResponsePlaylist, IResponsePlaylistItem, TBasicVideoUrlsMap } from "~/types";
import type { IPixellotPlayerSourceType } from "~~/modules/pixellot-sdk";
import formatEvent from "~/videos/helpers/format-event";

export function formatPlaylist(item: IResponsePlaylist): IPlaylist {
  return {
    id: item.id,
    name: item.attributes.name || "-",
    type: item.attributes.type || "private",
    size: item.attributes.size || 0,
    source_type: "playlist",
    is_favorite: Boolean(item.relationships?.favorite),
    favoriteId: item.relationships?.favorite?.id,
    permissions: {
      canEdit: item.meta?.permissions?.includes("write") || false,
      canDelete: item.meta?.permissions?.includes("delete") || false,
      canAddToFavorites: item.meta?.permissions?.includes("favorite") || false,
      canCreateReview: item.meta?.permissions?.includes("review") || false,
    },
  };
}

export function formatPlaylistItem(item: IResponsePlaylistItem, playlistId: string): IPlaylistItem {
  const event = formatEvent(item.attributes.source);
  return {
    id: item.id,
    playlistId,
    type: item.type,
    title: item.attributes.name || "",
    playerNumber: item.attributes.player || "",
    startTime: item.attributes.startTime,
    endTime: item.attributes.endTime,
    playlist_source: event,
    event,
    permissions: {
      canEdit: item.meta.permissions?.includes("write") || false,
      canDelete: item.meta.permissions?.includes("delete") || false,
      canCreateReview: item.meta.permissions?.includes("review") || false,
      canUseSourceURLInsteadOfSourceClipping: false,
    },
    telestrations:
      item.attributes.telestrations?.map((telestration) => ({
        targetId: telestration.attributes.targetId,
        eventId: telestration.attributes.eventId,
        owner: telestration.attributes.owner,
        start: telestration.attributes.start,
        body: telestration.attributes.body,
        id: telestration.id,
      })) || [],
  };
}

export function formatPlaylistItemV2(item: components["schemas"]["PlaylistItemV2"], playlistId: string, event: IEvent): IPlaylistItem {
  const sourceUrls: TBasicVideoUrlsMap = new Map();
  const angle = (item.attributes.angle as IPixellotPlayerSourceType) ?? ("hd" as IPixellotPlayerSourceType);

  sourceUrls.set(angle, {
    url: item.attributes.source?.attributes.url ?? "",
    type: angle,
    thumbnails_url: item.attributes.source?.attributes.previewThumbnail,
  });

  return {
    id: item.id,
    playlistId,
    type: item.type as "playlist-item",
    title: item.attributes.name || "",
    playerNumber: item.attributes.player || "",
    startTime: item.attributes.startTime || 0,
    endTime: item.attributes.endTime || 0,
    event,
    playlist_source: {
      id: item.attributes.source?.id || item.id,
      eventId: item.attributes.sourceId || "",
      type: "clip", // Technically it's a clip, just need to ensure we're consistent with the type
      source_type: "video",
      title: item.attributes.name || "",
      created_at: item.meta?.createdAt || "",
      views_count: 0,
      video_duration: 0,
      poster: item.attributes.source?.attributes.thumbnail,
      urls: sourceUrls,
      is_favorite: false,
      permissions: {
        canAddToFavorites: false,
        canDownloadVideoWithoutLogin: false,
      },
    },
    permissions: {
      canEdit: item.meta?.permissions?.includes("write") || false,
      canDelete: item.meta?.permissions?.includes("delete") || false,
      canCreateReview: item.meta?.permissions?.includes("review") || false,
      // In the new v2 API we're not using source clipping anymore, we're using source URL instead
      canUseSourceURLInsteadOfSourceClipping: true,
    },
    // The v2 API doesn't return telestrations
    // Usually we expect the v2 items to be called on event level for system playlists
    // So we don't expect telestrations to be present
    telestrations: [],
  };
}

export function formatClickableStatToSystemPlaylist(item: { id: string; name: string }): IPlaylist {
  return {
    id: item.id,
    type: "system",
    name: item.name || "",
    size: 0,
    source_type: "playlist",
    is_favorite: false,
    permissions: {
      canEdit: false,
      canDelete: false,
      canAddToFavorites: false,
      canCreateReview: false,
    },
  };
}
